import { useEffect, useRef, useState } from "react";
import {
  Alert, Box, Card, MenuItem, Select, TextField, Typography,
  Table, TableBody, TableCell, TableHead, TableRow, IconButton,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { adminApi } from "rsa/adminApi";
import SectionHeader from "../Components/SectionHeader";
import StatusChip from "../Components/StatusChip";
import RequestDetailDrawer from "./RequestDetailDrawer";

const SEVERITY_COLOR = { critical: "#dc2626", high: "#ea580c", medium: "#2563eb", low: "#6b7280" };

// One glyph per pickup pin — ambulance outranks fire outranks police when several are flagged.
const requestGlyph = (r) =>
  r.ambulance_requested ? "🚑" : r.fire_requested ? "🚒" : r.police_requested ? "👮" : "📍";

const EmergencyBadges = ({ r }) => (
  <>
    {r.ambulance_requested && <span title="Ambulance requested">🚑</span>}
    {r.fire_requested && <span title="Fire brigade requested">🚒</span>}
    {r.police_requested && <span title="Police requested">👮</span>}
  </>
);

const pin = (color, glyph) => L.divIcon({
  className: "",
  html: `<div style="width:26px;height:26px;border-radius:50%;background:${color};display:flex;align-items:center;justify-content:center;color:#fff;font-size:13px;box-shadow:0 0 0 3px rgba(0,0,0,0.15)">${glyph}</div>`,
  iconSize: [26, 26], iconAnchor: [13, 13],
});

function OpsMap({ requests }) {
  const elRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);

  useEffect(() => {
    if (mapRef.current || !elRef.current) return;
    mapRef.current = L.map(elRef.current, { zoomControl: true, attributionControl: false }).setView([19.076, 72.8777], 11);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19 }).addTo(mapRef.current);
    layerRef.current = L.layerGroup().addTo(mapRef.current);
    return () => { mapRef.current?.remove(); mapRef.current = null; };
  }, []);

  useEffect(() => {
    const map = mapRef.current, group = layerRef.current;
    if (!map || !group) return;
    group.clearLayers();
    const bounds = [];
    requests.forEach((r) => {
      if (r.pickup_latitude == null) return;
      const color = SEVERITY_COLOR[r.severity] || SEVERITY_COLOR.low;
      L.marker([r.pickup_latitude, r.pickup_longitude], { icon: pin(color, requestGlyph(r)) })
        .bindTooltip(`${r.request_ref} · ${r.service_type} · ${r.requester_name}`, { direction: "top" })
        .addTo(group);
      bounds.push([r.pickup_latitude, r.pickup_longitude]);
      if (r.vendor_latitude != null) {
        L.marker([r.vendor_latitude, r.vendor_longitude], { icon: pin("#0f766e", "🔧") })
          .bindTooltip(r.vendor_name, { direction: "top" })
          .addTo(group);
        bounds.push([r.vendor_latitude, r.vendor_longitude]);
      }
    });
    if (bounds.length) map.fitBounds(bounds, { padding: [30, 30], maxZoom: 14 });
    setTimeout(() => map.invalidateSize(), 60);
  }, [requests]);

  return <div ref={elRef} style={{ height: 420, width: "100%", borderRadius: 15 }} />;
}

const cardSx = { borderRadius: "15px", boxShadow: "rgba(0,0,0,0.1) 0px 10px 15px -3px, rgba(0,0,0,0.05) 0px 4px 6px -2px", p: 2 };

export default function LiveOpsPage() {
  const [live, setLive] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ status: "", severity: "", requesterType: "" });
  const [error, setError] = useState("");
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    try {
      setError("");
      const qs = new URLSearchParams(Object.fromEntries(Object.entries(filters).filter(([, v]) => v))).toString();
      const [liveData, alertData, rowData] = await Promise.all([
        adminApi("GET", "/admin/rsa/requests/live"),
        adminApi("GET", "/admin/rsa/alerts"),
        adminApi("GET", `/admin/rsa/requests${qs ? `?${qs}` : ""}`),
      ]);
      setLive(liveData); setAlerts(alertData); setRows(rowData);
    } catch (e) {
      setError(e.status === 401 ? "Session expired — sign in again." : e.message);
    }
  };

  useEffect(() => { load(); }, [filters]); // eslint-disable-line

  const setFilter = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));

  return (
    <Box>
      <SectionHeader
        title="Live Ops"
        action={<IconButton onClick={load}><RefreshIcon /></IconButton>}
      />
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {alerts.length > 0 && (
        <Box mb={2} display="flex" gap={1.5} flexWrap="wrap">
          {alerts.map((a) => (
            <Card key={a.id} onClick={() => setOpenId(a.id)}
              sx={{ ...cardSx, p: 1.5, minWidth: 260, cursor: "pointer", border: "1px solid #dc2626", background: "#fef2f2" }}>
              <Box display="flex" alignItems="center" gap={1}>
                <EmergencyBadges r={a} />
                <Typography fontWeight={700} color="#991b1b">{a.request_ref}</Typography>
                <StatusChip value={a.severity} />
              </Box>
              <Typography variant="body2">{a.service_type} · {a.requester_name}</Typography>
              <Typography variant="caption" color="text.secondary">{a.vendor_name || "Unassigned"}</Typography>
            </Card>
          ))}
        </Box>
      )}

      <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
        <Card sx={{ ...cardSx, flex: "1 1 380px" }}>
          <Typography variant="subtitle1" fontWeight={600} mb={1}>Active requests map</Typography>
          <OpsMap requests={live} />
        </Card>

        <Card sx={{ ...cardSx, flex: "2 1 520px" }}>
          <Box display="flex" gap={1.5} mb={1.5} flexWrap="wrap">
            <Select displayEmpty size="small" value={filters.status} onChange={setFilter("status")} sx={{ minWidth: 140 }}>
              <MenuItem value="">All statuses</MenuItem>
              {["created", "broadcasting", "claimed", "enroute", "arrived", "in_progress", "completed", "cancelled", "expired"].map((s) => (
                <MenuItem key={s} value={s}>{s.replace(/_/g, " ")}</MenuItem>
              ))}
            </Select>
            <Select displayEmpty size="small" value={filters.severity} onChange={setFilter("severity")} sx={{ minWidth: 140 }}>
              <MenuItem value="">All severities</MenuItem>
              {["low", "medium", "high", "critical"].map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
            <Select displayEmpty size="small" value={filters.requesterType} onChange={setFilter("requesterType")} sx={{ minWidth: 140 }}>
              <MenuItem value="">Guest + Registered</MenuItem>
              <MenuItem value="registered">Registered</MenuItem>
              <MenuItem value="guest">Guest</MenuItem>
            </Select>
            <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} onChange={setFilter("from")} />
            <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} onChange={setFilter("to")} />
          </Box>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Ref</TableCell><TableCell>Type</TableCell><TableCell>Requester</TableCell>
                <TableCell>Sev</TableCell><TableCell>Status</TableCell><TableCell>Vendor</TableCell><TableCell>Fee</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && <TableRow><TableCell colSpan={7} align="center">No requests</TableCell></TableRow>}
              {rows.map((r) => (
                <TableRow key={r.id} hover sx={{ cursor: "pointer" }} onClick={() => setOpenId(r.id)}>
                  <TableCell>{r.request_ref} <EmergencyBadges r={r} /></TableCell>
                  <TableCell>{r.service_type}</TableCell>
                  <TableCell>{r.requester_name} <Typography component="span" variant="caption" color="text.secondary">({r.requester_type})</Typography></TableCell>
                  <TableCell><StatusChip value={r.severity} /></TableCell>
                  <TableCell><StatusChip value={r.status} /></TableCell>
                  <TableCell>{r.vendor_name || "—"}</TableCell>
                  <TableCell>{r.final_fee ? `₹${Math.round(r.final_fee)}` : r.estimated_fee ? `~₹${Math.round(r.estimated_fee)}` : "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </Box>

      {openId != null && (
        <RequestDetailDrawer id={openId} onClose={() => setOpenId(null)} onChanged={load} />
      )}
    </Box>
  );
}
